/**
 * TenantAgents - Página de gerenciamento de agentes do tenant
 * 
 * Funcionalidades:
 * - Listar membros da equipe com status (ativo/inativo)
 * - Editar função (Agente/Supervisor/Administrador) e status de agentes
 * - Excluir agentes que nunca tiveram conversas
 * - Convidar novos agentes via email
 * - Gerenciar convites pendentes (cancelar/reenviar)
 *
 * RELEASE: 2026-10-22 v1.3.0 - Busca centralizada no cabeçalho (sem subtítulo) e filtros
 *          rápidos Função/Perfil/Status, filtrando localmente a lista já carregada.
 * RELEASE: 2026-10-22 v1.3.1 - Campo de busca adota o leiaute premium padrão das listagens
 *          (Tarefas/Negócios/O.S.): caixa arredondada sem borda com fundo muted.
 * RELEASE: 2026-10-22 v1.3.2 - Trilha central do cabeçalho definida em minmax(0,28rem) para a
 *          busca renderizar com a mesma largura do campo da listagem de Tarefas (448px).
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import { 
  UserPlus, 
  Mail, 
  Clock, 
  X, 
  RefreshCw, 
  Users, 
  Send, 
  Loader2, 
  Pencil, 
  Trash2,
  Crown,
  UserCheck,
  MoreVertical,
  Calendar,
  Shield,
  ShieldCheck,
  ShieldAlert,
  User,
  Building2,
  Search
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';
import EditAgentDialog from '@/components/tenant/agents/EditAgentDialog';
import { ListPageFiltersBar, LIST_FILTERS_SELECT_TRIGGER_CLASS } from '@/components/layout/list-page/ListPageFiltersBar';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';

// ============================================
// INTERFACES
// ============================================

interface Agent {
  id: string;
  user_id: string;
  is_owner: boolean;
  is_active: boolean;
  joined_at: string;
  email?: string;
  full_name?: string;
  avatar_url?: string | null; // URL do avatar (ID 3D ou path de storage)
  can_delete?: boolean;
  role?: string;
  tenant_count?: number; // Quantidade de tenants vinculados
  permission_profile_name?: string; // v1.1.0: Nome do perfil de permissão
  // v1.2.0: Sinalização White Label
  is_wl_operator?: boolean; // Membro é operador WL provisionado via painel White Label
  wl_support_session_active?: boolean; // Sessão de Suporte WL ativa neste tenant agora
}


interface Invite {
  id: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
  expires_at: string;
}

// Interface para contagens locais (membros e convites pendentes)
interface AgentCounts {
  current_agents: number;
  pending_invites: number;
}

interface PermissionProfile {
  id: string;
  name: string;
}

// ============================================
// CONFIGURAÇÕES DE ESTILO
// ============================================

// Configuração visual por função
const roleConfig: Record<string, {
  label: string;
  borderColor: string;
  badgeClass: string;
  iconClass: string;
  icon: typeof Shield;
}> = {
  tenant_admin: {
    label: 'Administrador',
    borderColor: 'border-t-primary',
    badgeClass: 'bg-primary/10 text-primary border-primary/30',
    iconClass: 'text-primary',
    icon: ShieldCheck,
  },
  tenant_supervisor: {
    label: 'Supervisor',
    borderColor: 'border-t-purple-500',
    badgeClass: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
    iconClass: 'text-purple-600',
    icon: Shield,
  },
  agent: {
    label: 'Agente',
    borderColor: 'border-t-muted-foreground/50',
    badgeClass: 'bg-muted text-muted-foreground border-muted-foreground/30',
    iconClass: 'text-muted-foreground',
    icon: User,
  },
};

// Configuração visual por status
const statusConfig: Record<string, {
  label: string;
  badgeClass: string;
  dotClass: string;
}> = {
  active: {
    label: 'Ativo',
    badgeClass: 'bg-green-500/10 text-green-600 border-green-500/30',
    dotClass: 'bg-green-500',
  },
  inactive: {
    label: 'Inativo',
    badgeClass: 'bg-red-500/10 text-red-600 border-red-500/30',
    dotClass: 'bg-red-500',
  },
};

// ============================================
// COMPONENTES AUXILIARES
// ============================================

/**
 * MetricCard - Card de KPI compacto horizontal (padrão unificado DealListKpiCards)
 */
interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  variant: 'primary' | 'success' | 'warning' | 'info';
  subtitle?: string;
}

/** Mapeamento de variantes para cores (padrão DealListKpiCards) */
const agentVariantStyles = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  warning: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
};

const MetricCard = ({ title, value, icon: Icon, variant, subtitle }: MetricCardProps) => {
  const styles = agentVariantStyles[variant];
  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">{value}</p>
        </div>
        {subtitle && <p className="text-[10px] text-gray-600 dark:text-gray-400 mt-1 truncate">{subtitle}</p>}
      </CardContent>
    </Card>
  );
};

/**
 * EmptyState - Estado vazio com design moderno
 */
interface EmptyStateProps {
  icon: React.ElementType;
  iconBgColor: string;
  iconColor: string;
  title: string;
  description: string;
  action?: React.ReactNode;
}

const EmptyState = ({ icon: Icon, iconBgColor, iconColor, title, description, action }: EmptyStateProps) => (
  <div className="border-dashed border-2 rounded-xl bg-gradient-to-br from-muted/30 to-muted/10 flex flex-col items-center justify-center py-16">
    <div className={`flex items-center justify-center w-16 h-16 rounded-full ${iconBgColor} mb-4`}>
      <Icon className={`h-8 w-8 ${iconColor}`} />
    </div>
    <h3 className="text-lg font-semibold mb-2">{title}</h3>
    <p className="text-muted-foreground text-center max-w-md mb-4">{description}</p>
    {action}
  </div>
);

/**
 * getInitials - Obtém iniciais do nome
 */
const getInitials = (name?: string, email?: string) => {
  if (name) {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  }
  return email?.substring(0, 2).toUpperCase() || '??';
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const TenantAgents = () => {
  const { currentTenant, session, user } = useAuth();
  
  // Hook de permissões granulares para supervisores
  const { canCreate, canEdit, canDelete } = usePermissions();
  
  // Hook de limites efetivos (plano base + add-ons)
  const { limits: resourceLimits, refetchLimits, isLoadingLimits } = useResourceLimits();
  
  // Estados de dados
  const [agents, setAgents] = useState<Agent[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
   const [counts, setCounts] = useState<AgentCounts | null>(null);
  const [profiles, setProfiles] = useState<PermissionProfile[]>([]);
  
  // Estados de loading
  const [loading, setLoading] = useState(true);

  // v1.3.0: busca e filtros rápidos da listagem de membros
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [profileFilter, setProfileFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sending, setSending] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  // Estados de dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  // Auto-abre modal quando vindo do botão 'Novo' do header (Quick Create)
  useAutoOpenFromQuery('1', () => setDialogOpen(true));
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  
  // Estados do formulário de convite
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('agent');
  const [inviteProfileId, setInviteProfileId] = useState<string>('');

  // Carrega dados quando o tenant muda
  useEffect(() => {
    if (currentTenant?.id) {
      fetchData();
      fetchProfiles();
    }
  }, [currentTenant?.id]);

  // ============================================
  // FUNÇÕES DE BUSCA DE DADOS
  // ============================================

  const fetchProfiles = async () => {
    if (!currentTenant?.id) return;
    try {
      // Server-side: substitui SELECT direto em permission_profiles por RPC
      const { data, error } = await supabase.rpc('get_permission_profiles_list', {
        p_tenant_hint: currentTenant.id,
      });
      if (!error) {
        // RPC retorna mais campos; mantemos apenas os ativos para o select
        const activeOnly = (data || [])
          .filter((p: any) => p.is_active)
          .map((p: any) => ({ id: p.id, name: p.name }));
        setProfiles(activeOnly);
      }
    } catch (error) {
      console.error('Erro ao buscar perfis:', error);
    }
  };

  const fetchData = async () => {
    if (!currentTenant?.id) return;
    setLoading(true);

     try {
       // Busca agentes, convites e contadores via RPCs seguras (sem SELECT direto em tenants)
       await Promise.all([fetchAgents(), fetchInvites(), fetchCounts()]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * fetchAgents - Busca lista de agentes via RPC consolidada
   * Substitui múltiplas queries diretas por uma única chamada RPC
   * que retorna dados de perfil, role, tenant_count e can_delete
   */
  const fetchAgents = async () => {
    if (!currentTenant?.id) return;

    try {
      const { data, error } = await supabase.rpc('get_tenant_agents_list', {
        p_tenant_hint: currentTenant.id
      });

      if (error) throw error;

      // Mapeia resultado da RPC para a interface Agent do frontend
      // Mapeia resultado da RPC para a interface Agent do frontend
      // v1.1.0: Adicionado permission_profile_name
      const formattedAgents: Agent[] = (data || []).map((item: any) => ({
        id: item.member_id,
        user_id: item.user_id,
        is_owner: item.is_owner || false,
        is_active: item.is_active ?? true,
        joined_at: item.joined_at,
        email: item.email,
        full_name: item.full_name || undefined,
        avatar_url: item.avatar_url || null,
        can_delete: item.can_delete,
        role: item.role || 'agent',
        tenant_count: Number(item.tenant_count) || 1,
        permission_profile_name: item.permission_profile_name || undefined,
        // v1.2.0: Flags White Label
        is_wl_operator: Boolean(item.is_wl_operator),
        wl_support_session_active: Boolean(item.wl_support_session_active),
      }));


      setAgents(formattedAgents);
    } catch (error) {
      console.error('Erro ao buscar agentes:', error);
    }
  };

  const fetchInvites = async () => {
    if (!currentTenant?.id) return;

    try {
      // Server-side: substitui SELECT direto em tenant_invites por RPC
      const { data, error } = await supabase.rpc('get_tenant_invites_list', {
        p_tenant_hint: currentTenant.id,
        p_status_filter: ['pending'],
      });

      if (error) throw error;
      setInvites((data || []) as any);
    } catch (error) {
      console.error('Erro ao buscar convites:', error);
    }
  };

  /**
   * Busca contagens locais de membros e convites pendentes
   * O limite máximo vem do hook useResourceLimits (plano base + add-ons)
   */
  const fetchCounts = async () => {
    if (!currentTenant?.id) return;

    try {
      // Server-side: substitui 2 SELECTs (tenant_members + tenant_invites) por 1 RPC
      const { data, error } = await supabase.rpc('get_tenant_agents_counts', {
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;
      const row = (data || [])[0] as any;
      setCounts({
        current_agents: Number(row?.current_agents) || 0,
        pending_invites: Number(row?.pending_invites) || 0,
      });
    } catch (error) {
      console.error('Erro ao buscar contagens:', error);
    }
  };

  // ============================================
  // HANDLERS DE AÇÕES
  // ============================================

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Digite o email do agente');
      return;
    }

    if (!session?.access_token) {
      toast.error('Sessão expirada. Faça login novamente.');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            tenant_id: currentTenant?.id,
            email: inviteEmail.trim().toLowerCase(),
            role: inviteRole,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        toast.error(result.error || 'Erro ao enviar convite');
        return;
      }

      toast.success('Convite enviado com sucesso!');
      setDialogOpen(false);
      setInviteEmail('');
      setInviteRole('agent');
      fetchData();
    } catch (error) {
      console.error('Erro ao enviar convite:', error);
      toast.error('Erro ao enviar convite');
    } finally {
      setSending(false);
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    setCancellingId(inviteId);
    try {
      // Server-side: substitui UPDATE direto em tenant_invites por RPC
      const { error } = await supabase.rpc('cancel_tenant_invite', {
        p_invite_id: inviteId,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      if (error) throw error;

      toast.success('Convite cancelado');
      fetchData();
    } catch (error) {
      console.error('Erro ao cancelar convite:', error);
      toast.error('Erro ao cancelar convite');
    } finally {
      setCancellingId(null);
    }
  };

  const handleResendInvite = async (invite: Invite) => {
    if (!session?.access_token) {
      toast.error('Sessão expirada');
      return;
    }

    setResendingId(invite.id);
    try {
      // Server-side: substitui UPDATE direto em tenant_invites por RPC
      await supabase.rpc('cancel_tenant_invite', {
        p_invite_id: invite.id,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            tenant_id: currentTenant?.id,
            email: invite.email,
            role: invite.role,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        toast.error(result.error || 'Erro ao reenviar convite');
        return;
      }

      toast.success('Convite reenviado!');
      fetchData();
    } catch (error) {
      console.error('Erro ao reenviar convite:', error);
      toast.error('Erro ao reenviar convite');
    } finally {
      setResendingId(null);
    }
  };

  const handleEditAgent = (agent: Agent) => {
    setSelectedAgent(agent);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (agent: Agent) => {
    setSelectedAgent(agent);
    setDeleteDialogOpen(true);
  };

  const handleDeleteAgent = async () => {
    if (!selectedAgent || !currentTenant?.id) return;

    // Usa flag can_delete já calculada pela RPC get_tenant_agents_list
    if (!selectedAgent.can_delete) {
      toast.error('Este agente não pode ser excluído pois já foi vinculado a conversas');
      setDeleteDialogOpen(false);
      return;
    }

    setDeletingId(selectedAgent.id);
    try {
      // Server-side: substitui DELETEs diretos em tenant_members + user_roles
      // por RPC atômica que valida tenant, owner e auto-remoção.
      const { error: memberError } = await supabase.rpc('delete_tenant_member', {
        p_member_id: selectedAgent.id,
        p_tenant_hint: currentTenant.id,
      });

      if (memberError) throw memberError;

      toast.success('Agente removido com sucesso');
      setDeleteDialogOpen(false);
      setSelectedAgent(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir agente:', error);
      toast.error('Erro ao excluir agente');
    } finally {
      setDeletingId(null);
    }
  };

  // Calcula limites efetivos (plano base + add-ons)
  const maxAgents = resourceLimits?.effective_max_agents ?? 0;
  const isEnterprise = resourceLimits?.is_enterprise ?? false;
  const currentAgentCount = counts?.current_agents ?? 0;
  const pendingInviteCount = counts?.pending_invites ?? 0;

  // Verifica se pode convidar mais agentes (considerando add-ons)
  const canInvite = isEnterprise || (currentAgentCount + pendingInviteCount) < maxAgents;

   // Encontra o proprietário original usando is_owner da RPC get_tenant_agents_list
   const originalOwner = agents.find(a => a.is_owner);

  // Conta agentes ativos
  const activeAgents = agents.filter(a => a.is_active).length;

  // v1.3.0: filtragem local sobre a lista já carregada (sem nova consulta)
  const normalizedSearch = search.trim().toLowerCase();
  const filteredAgents = agents.filter((a) => {
    if (normalizedSearch && !`${a.full_name ?? ''} ${a.email ?? ''}`.toLowerCase().includes(normalizedSearch)) return false;
    if (roleFilter !== 'all' && (a.role || 'agent') !== roleFilter) return false;
    if (profileFilter === '__none__' && a.permission_profile_name) return false;
    if (profileFilter !== 'all' && profileFilter !== '__none__' && a.permission_profile_name !== profileFilter) return false;
    if (statusFilter === 'active' && !a.is_active) return false;
    if (statusFilter === 'inactive' && a.is_active) return false;
    return true;
  });
  const hasActiveFilters = roleFilter !== 'all' || profileFilter !== 'all' || statusFilter !== 'all' || normalizedSearch !== '';

  // ============================================
  // RENDERIZAÇÃO
  // ============================================

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto - padrão Contatos */}
      {/* v1.3.0: grid 3 colunas — título | busca centralizada | ações (sem subtítulo) */}
      {/* v1.3.2: a trilha central do grid passou de `auto` para `minmax(0,28rem)` — com `auto`
          a coluna media apenas a largura intrínseca do input (~160px) e o placeholder quebrava.
          Assim a busca ocupa os mesmos 28rem (448px) do campo da listagem de Tarefas. */}
      <div className="flex flex-col gap-3 p-4 border-b sm:grid sm:grid-cols-[1fr_minmax(0,28rem)_1fr] sm:items-center">
        <h1 className="text-xl font-semibold">Agentes</h1>
        {/* v1.3.1: busca centralizada com o mesmo leiaute premium da listagem de Tarefas
            (caixa arredondada sem borda, fundo muted e ícone deslocado para a esquerda) */}
        <div className="relative w-full max-w-md mx-auto">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value.slice(0, 100))}
            maxLength={100}
            placeholder="Buscar por nome ou e-mail"
            className="h-10 pl-10 pr-4 text-sm rounded-xl border-none bg-muted/40 hover:bg-muted/60 transition-all duration-200 focus-visible:bg-background focus-visible:shadow-md focus-visible:ring-1 focus-visible:ring-primary/20 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50"
          />
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-self-end">
          {/* Dialog de convite - visível apenas para quem pode criar agentes */}
          {canCreate('agents') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  size="xs"
                  disabled={!canInvite}
                >
                  <UserPlus className="mr-1.5" />
                  Convidar Agente
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg w-[95vw] flex flex-col p-0 gap-0 overflow-hidden">
              {/* Capa premium padronizada (mesmo padrão do modal de anotações do chat) */}
              <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
                <div className="flex items-start gap-3 min-w-0 pr-8">
                  <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg shrink-0">
                    <UserPlus className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <DialogTitle className="truncate">Convidar Agente</DialogTitle>
                    <DialogDescription className="text-xs line-clamp-2 sm:line-clamp-none">
                      Envie um convite por email para um novo membro da equipe.
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              {/* Corpo */}
              <div className="flex-1 overflow-auto px-6 py-5 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="agente@empresa.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Função</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a função" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agent">Agente</SelectItem>
                      <SelectItem value="tenant_supervisor">Supervisor</SelectItem>
                      <SelectItem value="tenant_admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Rodapé */}
              <DialogFooter className="px-6 py-4 border-t bg-muted/30">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleInvite} disabled={sending}>
                  {sending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Enviar Convite
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
        <MetricCard
          title="Total Membros"
          value={agents.length}
          icon={Users}
          variant="primary"
        />
        <MetricCard
          title="Ativos"
          value={activeAgents}
          icon={UserCheck}
          variant="success"
        />
        <MetricCard
          title="Limite do Plano"
          value={resourceLimits ? `${currentAgentCount} / ${maxAgents}` : '-'}
          icon={Crown}
          variant="warning"
          subtitle={!canInvite && !isEnterprise ? 'Limite atingido' : undefined}
        />
        <MetricCard
          title="Convites Pendentes"
          value={pendingInviteCount}
          icon={Mail}
          variant="info"
        />
      </div>

      {/* Tabs de Membros e Convites */}
      <Tabs defaultValue="members">
        <TabsList className="bg-muted/50 p-1 rounded-lg">
          <TabsTrigger 
            value="members" 
            className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md transition-all"
          >
            <Users className="h-4 w-4" />
            Membros ({agents.length})
          </TabsTrigger>
          <TabsTrigger 
            value="invites" 
            className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md transition-all"
          >
            <Mail className="h-4 w-4" />
            Convites Pendentes ({invites.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab de Membros */}
        <TabsContent value="members" className="mt-4">
          {/* v1.3.0: filtros rápidos (Função, Perfil, Status) — estilo pill padrão */}
          {agents.length > 0 && (
            <ListPageFiltersBar
              className="mb-3 rounded-xl border"
              rightSlot={hasActiveFilters ? (
                <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] sm:h-8 sm:text-xs" onClick={() => { setRoleFilter('all'); setProfileFilter('all'); setStatusFilter('all'); setSearch(''); }}>
                  Limpar
                </Button>
              ) : undefined}
            >
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, QUICK_FILTER_PILL, 'sm:w-[150px]')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as funções</SelectItem>
                  {Object.entries(roleConfig).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={profileFilter} onValueChange={setProfileFilter}>
                <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, QUICK_FILTER_PILL, 'sm:w-[170px]')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os perfis</SelectItem>
                  <SelectItem value="__none__">Sem perfil</SelectItem>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, QUICK_FILTER_PILL, 'sm:w-[130px]')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="inactive">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </ListPageFiltersBar>
          )}

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : agents.length === 0 ? (
            <EmptyState
              icon={Users}
              iconBgColor="bg-primary/10"
              iconColor="text-primary/70"
              title="Nenhum membro cadastrado"
              description="Convide o primeiro membro para sua equipe de atendimento"
              action={
                canCreate('agents') ? (
                  <Button onClick={() => setDialogOpen(true)} disabled={!canInvite}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Convidar Primeiro Agente
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="rounded-xl border bg-gradient-to-br from-background to-muted/10 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/40">
                    <TableHead className="font-semibold">Agente</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Função</TableHead>
                    <TableHead className="font-semibold">Perfil</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Tenants</TableHead>
                    <TableHead className="font-semibold">Membro desde</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                   {/* v1.3.0: sem resultados para a busca/filtros aplicados */}
                   {filteredAgents.length === 0 && (
                     <TableRow>
                       <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-10">
                         Nenhum membro encontrado
                       </TableCell>
                     </TableRow>
                   )}
                   {filteredAgents.map((agent) => {
                      // Proprietário identificado via is_owner da RPC (sem SELECT direto em tenants)
                      const isOriginalOwner = agent.is_owner;
                    const isCurrentUser = agent.user_id === user?.id;
                    const role = roleConfig[agent.role || 'agent'] || roleConfig.agent;
                    const status = statusConfig[agent.is_active ? 'active' : 'inactive'];
                    const RoleIcon = role.icon;
                    const initials = getInitials(agent.full_name, agent.email);
                    
                    return (
                      <TableRow 
                        key={agent.id} 
                        className="group hover:bg-muted/20 transition-colors"
                      >
                        {/* Coluna: Agente (Avatar + Nome) */}
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <SmartAvatar
                              src={agent.avatar_url}
                              name={agent.full_name}
                              email={agent.email}
                              size="md"
                              className="border-2 border-muted"
                            />
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{agent.full_name || 'Sem nome'}</span>
                              {isOriginalOwner && (
                                <Badge variant="outline" className="text-yellow-600 border-yellow-500/30 bg-yellow-500/10 text-[10px] px-1.5 py-0">
                                  <Crown className="h-2.5 w-2.5 mr-0.5" />
                                  Proprietário
                                </Badge>
                              )}
                              {/* v1.2.0: Sinalização Operador White Label */}
                              {agent.is_wl_operator && (
                                <TooltipProvider delayDuration={150}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge
                                        variant="outline"
                                        className="text-amber-700 border-amber-500/40 bg-amber-500/10 text-[10px] px-1.5 py-0"
                                      >
                                        <ShieldAlert className="h-2.5 w-2.5 mr-0.5" />
                                        Operador WL
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Membro provisionado pelo painel White Label.<br />
                                      Não é um colaborador real do tenant — gerenciado externamente.
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {/* v1.2.0: Sessão de Suporte WL ativa neste tenant */}
                              {agent.wl_support_session_active && (
                                <TooltipProvider delayDuration={150}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge
                                        variant="outline"
                                        className="text-amber-700 border-amber-500/40 bg-amber-500/15 text-[10px] px-1.5 py-0"
                                      >
                                        <span className="relative mr-1 flex h-1.5 w-1.5">
                                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75" />
                                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-500" />
                                        </span>
                                        Sessão ativa
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Operador WL com Sessão de Suporte ativa neste tenant agora.
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </div>
                        </TableCell>

                        
                        {/* Coluna: Email */}
                        <TableCell className="text-muted-foreground text-sm">
                          {agent.email || '-'}
                        </TableCell>
                        
                        {/* Coluna: Função — apenas ícone + texto, sem badge */}
                        <TableCell>
                          <div className="flex items-center gap-1.5 text-sm">
                            <RoleIcon className={`h-3.5 w-3.5 ${role.iconClass}`} />
                            <span>{role.label}</span>
                            {agent.is_wl_operator && (
                              <span className="text-[10px] text-muted-foreground italic">
                                (via Suporte WL)
                              </span>
                            )}
                          </div>
                        </TableCell>

                        
                        {/* Coluna: Perfil de Permissão (v1.1.0) */}
                        <TableCell className="text-sm text-muted-foreground">
                          {agent.permission_profile_name || '-'}
                        </TableCell>
                        
                        {/* Coluna: Status — padrão Departamentos (Badge outline + StatusDot) */}
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                          >
                            <StatusDot tone={agent.is_active ? 'emerald' : 'muted'} />
                            {agent.is_active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        
                        {/* Coluna: Tenants Vinculados */}
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{agent.tenant_count || 1}</span>
                          </div>
                        </TableCell>
                        
                        {/* Coluna: Data de entrada */}
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(agent.joined_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                        
                        {/* Coluna: Ações */}
                        <TableCell>
                          {/* Permite editar a si mesmo (original owner) para configurações pessoais como Modo Observador */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                               {/* v1.2.0: Bloqueio total para Operador WL — gerenciado pelo painel White Label */}
                               {agent.is_wl_operator ? (
                                 <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                                   <ShieldAlert className="h-3.5 w-3.5 mr-2" />
                                   Operador WL — gerenciado pelo painel White Label
                                 </DropdownMenuItem>
                               ) : (
                                 <>
                                   {/* Opção Editar - visível apenas para quem pode editar agentes */}
                                   {/* CORREÇÃO: Bloqueia edição do proprietário original por outros admins */}
                                   {canEdit('agents') && (!isOriginalOwner || isCurrentUser) && (
                                     <DropdownMenuItem onClick={() => handleEditAgent(agent)}>
                                       <Pencil className="h-4 w-4 mr-2" />
                                       {isOriginalOwner && isCurrentUser ? 'Minhas Configurações' : 'Editar'}
                                     </DropdownMenuItem>
                                   )}
                                   {/* Opção Excluir - visível para quem pode deletar, exceto si mesmo ou original owner */}
                                   {canDelete('agents') && !isCurrentUser && !isOriginalOwner && (
                                     <>
                                       <DropdownMenuSeparator />
                                       <DropdownMenuItem 
                                         onClick={() => handleDeleteClick(agent)}
                                         disabled={!agent.can_delete || deletingId === agent.id}
                                         className="text-destructive focus:text-destructive"
                                       >
                                         {deletingId === agent.id ? (
                                           <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                         ) : (
                                           <Trash2 className="h-4 w-4 mr-2" />
                                         )}
                                         {agent.can_delete ? 'Excluir' : 'Não pode excluir'}
                                       </DropdownMenuItem>
                                     </>
                                   )}
                                 </>
                               )}

                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* Tab de Convites */}
        <TabsContent value="invites" className="mt-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : invites.length === 0 ? (
            <EmptyState
              icon={Mail}
              iconBgColor="bg-blue-500/10"
              iconColor="text-blue-500/70"
              title="Nenhum convite pendente"
              description="Todos os convites foram aceitos ou expiraram"
            />
          ) : (
            <div className="rounded-xl border bg-gradient-to-br from-background to-muted/10 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/40">
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Função</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Enviado em</TableHead>
                    <TableHead className="font-semibold">Expira em</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map((invite) => {
                    const role = roleConfig[invite.role] || roleConfig.agent;
                    const RoleIcon = role.icon;
                    
                    return (
                      <TableRow 
                        key={invite.id} 
                        className="group hover:bg-muted/20 transition-colors"
                      >
                        {/* Coluna: Email */}
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10 border-2 border-yellow-500/30">
                              <AvatarFallback className="bg-yellow-500/10 text-yellow-600 font-semibold">
                                <Mail className="h-4 w-4" />
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium">{invite.email}</span>
                          </div>
                        </TableCell>
                        
                        {/* Coluna: Função */}
                        <TableCell>
                          <Badge variant="outline" className={`${role.badgeClass} text-xs`}>
                            <RoleIcon className="h-3 w-3 mr-1" />
                            {role.label}
                          </Badge>
                        </TableCell>
                        
                        {/* Coluna: Status */}
                        <TableCell>
                          <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30 text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            Aguardando aceite
                          </Badge>
                        </TableCell>
                        
                        {/* Coluna: Enviado em */}
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(invite.created_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                        
                        {/* Coluna: Expira em */}
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(invite.expires_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                        
                        {/* Coluna: Ações */}
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {/* Opção Reenviar - visível para quem pode editar agentes */}
                              {canEdit('agents') && (
                                <DropdownMenuItem 
                                  onClick={() => handleResendInvite(invite)} 
                                  disabled={resendingId === invite.id}
                                >
                                  {resendingId === invite.id ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                  )}
                                  Reenviar
                                </DropdownMenuItem>
                              )}
                              {/* Opção Cancelar - visível para quem pode editar agentes */}
                              {canEdit('agents') && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => handleCancelInvite(invite.id)}
                                    disabled={cancellingId === invite.id}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    {cancellingId === invite.id ? (
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                      <X className="h-4 w-4 mr-2" />
                                    )}
                                    Cancelar
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog de Edição de Agente */}
      <EditAgentDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        agent={selectedAgent}
        currentUserId={user?.id || ''}
        tenantId={currentTenant?.id || ''}
        onSuccess={fetchData}
      />

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Agente</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover <strong>{selectedAgent?.full_name || selectedAgent?.email}</strong> da equipe?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAgent}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingId ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TenantAgents;
